import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import LanguageToggle from '../components/LanguageToggle'
import interactiveBusinessLogo from '../Logo/InteractiveBusinessLogo.svg'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('customer')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { signIn } = useAuth()
  const { t, i18n } = useTranslation('auth')
  
  // Dynamic article for proper English grammar
  const getRoleToggleText = () => {
    console.log('Current role:', role, 'Language:', i18n.language) // Debug log
    if (i18n.language === 'en') {
      // Directly construct the text based on role for 95% reliability
      return role === 'admin' ? 'I am an' : 'I am a'
    }
    return 'Soy un' // Spanish
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { error } = await signIn(email, password)
      if (error) throw error
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-3 flex flex-col justify-center sm:flex sm:items-center sm:justify-center sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-xl flex-col sm:max-w-md">
        <div className="mb-5 flex items-start justify-between gap-3 sm:hidden">
          <img
            src={interactiveBusinessLogo}
            alt="Interactive Business logo"
            className="h-10 w-auto"
          />

          <div className="flex items-center gap-2">
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
            >
              About
            </Link>
            <LanguageToggle />
          </div>
        </div>

        <div className="hidden sm:block absolute top-6 left-6">
          <img
            src={interactiveBusinessLogo}
            alt="Interactive Business logo"
            className="h-12 w-auto"
          />
        </div>

        <div className="hidden sm:flex absolute top-4 right-4 items-center gap-4">
          <Link
            to="/about"
            className="text-base font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
          >
            About
          </Link>
          <LanguageToggle />
        </div>

        <div className="w-full space-y-5 sm:space-y-10">
          <div>
            <h2 className="text-center text-3xl font-extrabold leading-tight text-gray-900 sm:mt-6 sm:text-4xl">
              <span className="text-[#FF1B54]">Interactive </span>
              <span className="text-gray-900">Business</span>
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 sm:mt-3 sm:text-base">
              {t('login.subtitle')}
            </p>
          </div>

          <div className="rounded-lg bg-white p-6 shadow sm:p-8">
            {/* Role Toggle */}
            <div className="mb-6">
              <p className="mb-2 text-sm text-gray-600">
                {getRoleToggleText()}
              </p>
              <div className="grid grid-cols-2 rounded-md bg-[#FFE5EC] p-1">
                <button
                  type="button"
                  onClick={() => setRole('customer')}
                  className={`py-2 px-3 text-sm font-medium rounded-md ${
                    role === 'customer'
                      ? 'bg-[#FF1B54] text-white shadow'
                      : 'text-[#FF1B54] hover:text-[#E1174C]'
                  }`}
                >
                  {t('login.roleCustomer')}
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={`py-2 px-3 text-sm font-medium rounded-md ${
                    role === 'admin'
                      ? 'bg-[#FF1B54] text-white shadow'
                      : 'text-[#FF1B54] hover:text-[#E1174C]'
                  }`}
                >
                  {t('login.roleAdmin')}
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="rounded bg-red-50 border border-red-200 px-4 py-3 text-red-700">
                  {error}
                </div>
              )}


              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('login.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('login.passwordLabel')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-md border border-transparent bg-[#FF1B54] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#E1174C] focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : t('login.signInButton')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/forgot-password" className="text-sm text-[#FF1B54] hover:text-[#E1174C]">
                {t('login.forgotPassword')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
